import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Star, Heart, Send, CheckCircle, Camera } from 'lucide-react';
import { submitStory } from '../lib/api';

export default function ShareStory() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [form, setForm] = useState({
    customer_name: '',
    hair_type: '',
    rating: 5,
    testimonial_text: '',
    results_summary: '',
  });

  const handleFileChange = (file, type) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (type === 'before') { setBeforePhoto(file); setBeforePreview(url); }
    else { setAfterPhoto(file); setAfterPreview(url); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await submitStory(form, beforePhoto, afterPhoto);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hairTypes = [
    'Type 1A Straight', 'Type 1B Straight', 'Type 1C Straight',
    'Type 2A Wavy', 'Type 2B Wavy', 'Type 2C Wavy',
    'Type 3A Curly', 'Type 3B Curly', 'Type 3C Curly',
    'Type 4A Coily', 'Type 4B Coily', 'Type 4C Coily',
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="share-story-page">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/images/coras-logo.png" alt="Cora's Naturals" className="h-12" />
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft size={14} className="mr-1" /> Back to Shop
          </Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {submitted ? (
          <Card className="shadow-lg border-0 text-center" data-testid="story-success">
            <CardContent className="py-16">
              <CheckCircle size={64} className="mx-auto mb-4 text-[#4A5D3A]" />
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', 'Georgia', serif" }}>Thank You!</h2>
              <p className="text-gray-600 mb-6">Your story has been submitted and is pending review. We'll feature it on our site once approved!</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/')} style={{ backgroundColor: '#4A5D3A' }} className="rounded-full">Back to Shop</Button>
                <Button variant="outline" onClick={() => { setSubmitted(false); setForm({ customer_name: '', hair_type: '', rating: 5, testimonial_text: '', results_summary: '' }); }} className="rounded-full">Submit Another</Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg border-0">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2"><Heart size={32} className="text-[#4A5D3A]" /></div>
              <CardTitle className="text-2xl" style={{ fontFamily: "'Playfair Display', 'Georgia', serif" }}>Share Your Story</CardTitle>
              <CardDescription>Tell us how Cora's Naturals transformed your hair journey</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm" data-testid="story-error">{error}</div>}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Your Name</Label>
                    <Input
                      placeholder="Jane Doe"
                      value={form.customer_name}
                      onChange={e => setForm({ ...form, customer_name: e.target.value })}
                      required
                      data-testid="story-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hair Type</Label>
                    <Select value={form.hair_type} onValueChange={val => setForm({ ...form, hair_type: val })}>
                      <SelectTrigger data-testid="story-hair-type"><SelectValue placeholder="Select hair type" /></SelectTrigger>
                      <SelectContent>
                        {hairTypes.map(ht => <SelectItem key={ht} value={ht}>{ht}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Rating</Label>
                  <div className="flex gap-1" data-testid="story-rating">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setForm({ ...form, rating: n })}
                        className="transition-transform hover:scale-110"
                      >
                        <Star size={28} className={n <= form.rating ? 'text-[#4A5D3A] fill-[#4A5D3A]' : 'text-gray-300'} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Your Story</Label>
                  <Textarea
                    placeholder="Tell us about your hair journey with Cora's Naturals..."
                    value={form.testimonial_text}
                    onChange={e => setForm({ ...form, testimonial_text: e.target.value })}
                    rows={5}
                    required
                    data-testid="story-text"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Results Summary</Label>
                  <Input
                    placeholder="e.g., Visible growth in 6 weeks"
                    value={form.results_summary}
                    onChange={e => setForm({ ...form, results_summary: e.target.value })}
                    required
                    data-testid="story-results"
                  />
                </div>

                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label>Before & After Photos <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="photo-upload-box" data-testid="before-photo-upload">
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleFileChange(e.target.files[0], 'before')} />
                      {beforePreview ? (
                        <img src={beforePreview} alt="Before" className="photo-preview" />
                      ) : (
                        <div className="photo-placeholder">
                          <Camera size={24} className="text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Before Photo</span>
                        </div>
                      )}
                    </label>
                    <label className="photo-upload-box" data-testid="after-photo-upload">
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleFileChange(e.target.files[0], 'after')} />
                      {afterPreview ? (
                        <img src={afterPreview} alt="After" className="photo-preview" />
                      ) : (
                        <div className="photo-placeholder">
                          <Camera size={24} className="text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">After Photo</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-full"
                  style={{ backgroundColor: '#4A5D3A' }}
                  disabled={loading}
                  data-testid="story-submit"
                >
                  <Send size={14} className="mr-2" />
                  {loading ? 'Submitting...' : 'Submit Your Story'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
